import { removeSpellCheckPopup } from './removeSpellCheckPopup';
import { getErrorCounts } from '../utils/getErrorCounts';
import { calculatePopupPosition } from './calculatePopupPosition';
import { createPopupTitle } from './createPopupTitle';
import { createPopupLegend } from './createPopupLegend';
import { createErrorItem } from './createErrorItem';
import { createPopupActions } from './createPopupActions';

/**
 * Show spell check suggestions popup
 * @param {fabric.Textbox} textbox
 * @param {Event} eventData
 */
export function showSpellCheckSuggestions(textbox, eventData) {
  // Remove any existing popup (force removal)
  removeSpellCheckPopup(true);

  // Store timestamp and target textbox when popup is created
  window._spellCheckPopupTimestamp = Date.now();
  window._spellCheckPopupTextbox = textbox;
  window._spellCheckPopupInteracted = false; // Track if user interacted with popup

  // Get matches using the SpellCheckTextbox method if available
  let matches = [];
  if (typeof textbox.getSpellCheckErrors === 'function') {
    matches = textbox.getSpellCheckErrors();
  } else {
    matches = textbox.spellCheckResults?.matches || [];
  }

  if (matches.length === 0) {
    return;
  }

  const counts = getErrorCounts(textbox);

  // Create popup container (initially hidden for measurement)
  const popup = document.createElement('div');
  popup.id = 'spellcheck-suggestions-popup';

  // Add to body temporarily for measurement
  document.body.appendChild(popup);

  // Add title with counts
  const title = createPopupTitle(counts);
  popup.appendChild(title);

  // Add legend if we have mixed types
  const legend = createPopupLegend(counts);
  if (legend) {
    popup.appendChild(legend);
  }

  // Separate fixable and informational errors
  const fixableMatches = matches.filter(
    (match) => match.replacements && match.replacements.length > 0
  );
  const informationalMatches = matches.filter(
    (match) => !match.replacements || match.replacements.length === 0
  );

  // Add fixable errors first
  fixableMatches.forEach((match, index) => {
    createErrorItem(textbox, match, popup);
    if (index < fixableMatches.length - 1 || informationalMatches.length > 0) {
      const separator = document.createElement('hr');
      popup.appendChild(separator);
    }
  });

  // Add informational errors
  informationalMatches.forEach((match, index) => {
    createErrorItem(textbox, match, popup);
    if (index < informationalMatches.length - 1) {
      const separator = document.createElement('hr');
      popup.appendChild(separator);
    }
  });

  // Add bottom actions section
  const actionsSection = createPopupActions(textbox, matches);
  popup.appendChild(actionsSection);

  // Measure popup dimensions
  const popupRect = popup.getBoundingClientRect();
  const popupWidth = popupRect.width;
  const popupHeight = popupRect.height;

  // Calculate optimal position
  const optimalPosition = calculatePopupPosition(
    eventData.clientX,
    eventData.clientY,
    popupWidth,
    popupHeight
  );

  // Apply the calculated position and make visible
  popup.style.left = `${optimalPosition.left}px`;
  popup.style.top = `${optimalPosition.top}px`;
  popup.style.visibility = 'visible';

  // Simple close handlers
  const closeHandler = (e) => {
    // Don't close on right-click
    if (e.type === 'click' && e.button === 2) {
      return;
    }

    if (
      (e.type === 'click' && !popup.contains(e.target)) ||
      (e.type === 'keydown' && e.key === 'Escape')
    ) {
      removeSpellCheckPopup(true); // Force removal when user intentionally closes
      document.removeEventListener('click', closeHandler);
      document.removeEventListener('keydown', closeHandler);
      document.removeEventListener('contextmenu', closeHandler);
    }
  };

  // Add close handlers with small delay
  setTimeout(() => {
    document.addEventListener('click', closeHandler);
    document.addEventListener('keydown', closeHandler);
    // Also listen for contextmenu to handle right-clicks properly
    document.addEventListener('contextmenu', closeHandler);
  }, 100);
}
